import React, { useState, useEffect } from 'react';
import { ArrowRight, Clock, CheckCircle, Zap, Calendar, FileText, Palette, Smartphone, Globe, Search, Share2, PieChart, Layers, Award, Target, Compass, ExternalLink, ChevronRight, LogOut, Sun, BookOpen } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/SummerWelcomeDashboard.css';

const SummerWelcomeDashboard = ({ user: propUser, organization: propOrganization }) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check for user and organization data in location state
    const locationUser = location.state?.userData;
    const locationOrganization = location.state?.organizationData;
    
    // Use either props or location state
    const user = propUser || locationUser;
    const organization = propOrganization || locationOrganization;

    // Summer features
    const summerFeatures = [
        { 
            icon: <Sun className="w-8 h-8 text-yellow-500" />, 
            title: "Summer Reading", 
            description: "Perfect time to launch your reading app for the summer season" 
        },
        { 
            icon: <BookOpen className="w-8 h-8 text-green-600" />, 
            title: "Content Library", 
            description: "Build your digital library with eBooks and audiobooks" 
        },
        { 
            icon: <Smartphone className="w-8 h-8 text-blue-600" />, 
            title: "Mobile Ready", 
            description: "Reach readers on any device, anywhere" 
        },
        { 
            icon: <Target className="w-8 h-8 text-purple-600" />, 
            title: "Engagement", 
            description: "Keep readers engaged with interactive features" 
        }
    ];

    // Handle user logout
    const handleLogout = async () => {
        try {
            // Make a server-side logout request
            await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include'
            });
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            // Clear localStorage and redirect
            localStorage.removeItem('userData');
            localStorage.removeItem('appDemoState');
            localStorage.removeItem('savedMaterialPalettes');
            window.location.href = '/';
        }
    };

    // Handle navigation to app wizard
    const handleGetStarted = () => {
        if (user) {
            navigate('/app-wizard');
        } else {
            navigate('/login');
        }
    };

    // Handle navigation to dashboard
    const handleGoToDashboard = () => {
        console.log('SummerWelcomeDashboard: Navigating to dashboard...');
        navigate('/dashboard?force=true', { state: { forceDashboard: true } });
    };

    return (
        <div className="summer-welcome-dashboard min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Sun className="w-8 h-8 text-yellow-500" />
                                <h1 className="text-2xl font-bold text-gray-900">Summer Welcome</h1>
                            </div>
                            {organization && (
                                <div className="text-sm text-gray-600">
                                    {organization.name}
                                </div>
                            )}
                        </div>
                        
                        <div className="flex items-center space-x-4">
                            {user && (
                                <>
                                    <span className="text-sm text-gray-600">
                                        Welcome, {user.name || user.email}
                                    </span>
                                    <button
                                        onClick={handleGoToDashboard}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        Dashboard
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
                                        title="Logout"
                                    >
                                        <LogOut className="w-5 h-5" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Hero Section */}
                <div className="text-center mb-16">
                    <div className="flex justify-center mb-6">
                        <div className="p-4 bg-yellow-100 rounded-full">
                            <Sun className="w-16 h-16 text-yellow-600" />
                        </div>
                    </div>
                    <h1 className="text-5xl font-bold text-gray-900 mb-6">
                        Welcome to Your Summer
                        <span className="text-yellow-600"> Publishing Journey</span>
                    </h1>
                    <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                        Transform your content into engaging digital experiences. 
                        Perfect for the summer reading season and beyond.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={handleGetStarted}
                            className="px-8 py-4 bg-yellow-600 text-white text-lg font-semibold rounded-lg hover:bg-yellow-700 transition-colors flex items-center justify-center"
                        >
                            Get Started
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </button>
                        
                        {user && organization && (
                            <button
                                onClick={handleGoToDashboard}
                                className="px-8 py-4 bg-white text-yellow-600 text-lg font-semibold rounded-lg border-2 border-yellow-600 hover:bg-yellow-50 transition-colors"
                            >
                                View Dashboard
                            </button>
                        )}
                    </div>
                </div>

                {/* Features Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                    {summerFeatures.map((feature, index) => (
                        <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                            <div className="flex justify-center mb-4">
                                {feature.icon}
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                                {feature.title}
                            </h3>
                            <p className="text-gray-600 text-center">
                                {feature.description}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Organization Info */}
                {organization && (
                    <div className="bg-white rounded-xl p-8 shadow-lg mb-16">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Organization</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    {organization.name}
                                </h3>
                                {organization.website_url && (
                                    <p className="text-gray-600 mb-2">
                                        <Globe className="w-4 h-4 inline mr-2" />
                                        <a 
                                            href={organization.website_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                        >
                                            {organization.website_url}
                                        </a>
                                    </p>
                                )}
                                <p className="text-sm text-gray-500">
                                    Organization ID: {organization.id}
                                </p>
                            </div>
                            <div className="flex justify-end">
                                <button
                                    onClick={handleGoToDashboard}
                                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                                >
                                    Manage Organization
                                    <ExternalLink className="w-4 h-4 ml-2" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Call to Action */}
                <div className="text-center bg-gradient-to-r from-yellow-600 to-orange-600 rounded-xl p-12 text-white">
                    <h2 className="text-3xl font-bold mb-4">Ready to Begin?</h2>
                    <p className="text-xl mb-8 opacity-90">
                        Start your digital publishing journey today
                    </p>
                    <button
                        onClick={handleGetStarted}
                        className="px-8 py-4 bg-white text-yellow-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center"
                    >
                        Launch App Builder
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SummerWelcomeDashboard; 